import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { useDispatch, useSelector } from 'react-redux';
import Loader from '../../shared/components/Loader';

import {
  createPaymentIntent,
  confirmPayment,
  selectSelectedPlan,
  selectSubcriptionLoading,
  selectError,
  selectClientSecret,
  setSelectedPlan,
  setBillingCycle
} from '../../../app/store/slices/subscriptionSlice';
import { selectCanvases, fetchUserCanvases } from '../../../app/store/slices/canvasSlice';
import { toast } from 'react-toastify';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const CheckoutForm = () => {
  const stripe = useStripe();
  const elements = useElements();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  
  const selectedPlan = useSelector(selectSelectedPlan);
  const loading = useSelector(selectSubcriptionLoading);
  const error = useSelector(selectError);
  let userCanvases = useSelector(selectCanvases);


  // Add ref for the payment form container
  const paymentFormRef = React.useRef(null);

  // Add scroll handler for PaymentElement changes
  useEffect(() => {
    userCanvases = dispatch(fetchUserCanvases()).unwrap();
  }, [dispatch]);
  const handleFormChange = () => {
    if (paymentFormRef.current) {
      paymentFormRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!stripe || !elements) return;

    try {
      const { paymentIntent, error: submitError } = await stripe.confirmPayment({
        elements,
        redirect: 'if_required',
      });

      if (submitError) {
        throw new Error(submitError.message);
      }      if (paymentIntent.status === 'succeeded') {
        await dispatch(confirmPayment({ paymentIntentId: paymentIntent.id })).unwrap();
        toast.success('Your Subscription is Successful!');

        if (!userCanvases ||  userCanvases.owned.length === 0) {
          navigate('/create-canvas');
        } else {
          navigate('/dashboard');
        }
      }
    } catch (err) {
      console.error('Payment failed:', err);
    }
  };

  const renderPrice = () => {
    if (!selectedPlan?.prices) {
      return 'N/A';
    }
    
    const billingCycle = selectedPlan.billingCycle?.toLowerCase();
    if (!billingCycle || !selectedPlan.prices[billingCycle]) {
      return 'N/A';
    }

    const price = selectedPlan.prices[billingCycle];
    return price.display || `$${price.amount}`;
  };

  return (
    <form onSubmit={handleSubmit} className="w-full">
      <div className="flex flex-col lg:flex-row gap-4 lg:gap-6">
        <div className="w-full lg:w-1/3 order-2 lg:order-1">
          <div className="bg-btn-secondary p-4 rounded-lg lg:sticky lg:top-4">
            <h3 className="text-sm font-semibold mb-2">Order Summary</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Plan:</span>
                <span className="font-medium">{selectedPlan?.name}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Billing:</span>
                <span className="font-medium capitalize">
                  {selectedPlan?.billingCycle}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Total:</span>
                <span className="font-semibold text-btn-primary">
                  {renderPrice()}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Payment Form Section - Main Content */}
        <div className="w-full lg:w-2/3 order-1 lg:order-2">
          <div className="bg-white rounded-lg p-4" ref={paymentFormRef}>
            <PaymentElement 
              className="min-h-[150px]" 
              options={{
                layout: 'tabs',
                fields: { billingDetails: 'auto' }
              }}
              onChange={handleFormChange}
            />
          </div>

          {error && (
            <div className="text-red-500 text-center my-3 p-2 bg-red-50 rounded-lg text-sm">
              {error}
            </div>
          )}
          
          <button
            type="submit"
            disabled={!stripe || loading}
            className="submit-btn mt-4 w-full lg:w-9/12 flex items-center justify-center gap-2 sticky bottom-4"
          >
            {loading ? (
              <>
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                  <path className="opacity-75" fill="currentColor" 
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                </svg>
                <span className="ml-2">Processing...</span>
              </>
            ) : (
              `Subscribe to ${selectedPlan?.name}`
            )}
          </button>
        </div>
      </div>
    </form>
  );
};

const Payment = () => {
  const selectedPlan = useSelector(selectSelectedPlan);
  const clientSecret = useSelector(selectClientSecret);
  const loading = useSelector(selectSubcriptionLoading);
  const error = useSelector(selectError);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();

  useEffect(() => {
    const { subscriptionId, planDetails, selectedPlanKey, billingCycle } = location.state || {};
    

    if (!subscriptionId || !selectedPlanKey || !billingCycle) {
      // console.error('Missing required data:', { 
      //   subscriptionId, 
      //   selectedPlanKey,
      //   billingCycle 
      // });
      toast.error('Missing subscription details');
      navigate('/user-subscription');
      return;
    }

    // Create a complete plan object with billing cycle
    const completePlanData = {
      ...planDetails,
      planKey: selectedPlanKey,
      billingCycle: billingCycle,
      name: planDetails.name,   
      prices: planDetails.prices 
    };

    // Dispatch both actions
    dispatch(setSelectedPlan(completePlanData));
    dispatch(setBillingCycle(billingCycle));

    const createIntent = async () => {
      try {
        await dispatch(createPaymentIntent({
          subscriptionId,
          planKey: selectedPlanKey,
          billingCycle
        })).unwrap();
      } catch (err) {
        console.error('Failed to create payment intent:', err);
        toast.error('Failed to initialize payment. Please try again.');
        navigate('/user-subscription');
      }
    };

    createIntent();
  }, [dispatch, navigate, location.state]);

  if (loading || !selectedPlan || !clientSecret) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <Loader />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="text-center">
          <div className="text-red-500 mb-4">
            <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <button 
            onClick={() => navigate('/user-subscription')} 
            className="submit-btn"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  const options = {
    clientSecret,
    appearance: {
      theme: 'stripe',
    },
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4 py-6 sm:px-6">
      <div className="card-setup w-full max-w-4xl">  
        <h1 className="text-lg sm:text-xl font-semibold text-center mb-4 sm:mb-6">
          Complete Payment
        </h1>
        <Elements stripe={stripePromise} options={options}>
          <CheckoutForm />
        </Elements>
      </div>
    </div>
  );
};

export default Payment;